import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Calendar,
  Clock,
  ExternalLink,
  MapPin,
  ShieldAlert,
  AlertTriangle,
  Info,
  CheckCircle2,
  Share2,
  Activity,
  Terminal,
} from 'lucide-react';
import { NWSAdapter } from '../services/nwsAdapter';
import { NormalizedAlert } from '../types/alert';
import { SeverityBadge } from '../components/common/SeverityBadge';
import { AlertMap } from '../components/map/AlertMap';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';
import { AdSlot } from '../components/common/AdSlot';
import { formatDateSafe, formatRelativeTime, isAlertActive, getSeverityVisuals } from '../../src/utils/formatters';
import { getPlainLanguageGuidance } from '../utils/plainLanguage';

export const AlertDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [alert, setAlert] = useState<NormalizedAlert | null>(null);
  const [relatedAlerts, setRelatedAlerts] = useState<NormalizedAlert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [lang, setLang] = useState<'en' | 'es'>('en');

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const data = await NWSAdapter.fetchAlertById(id);
        if (!data) {
          setError('The requested alert could not be found or has expired from official active records.');
        } else {
          setAlert(data);
          const all = await NWSAdapter.fetchActiveAlerts();
          const related = all.alerts
            .filter((a) => a.id !== data.id && (a.event === data.event || a.areaDesc.includes(data.areaDesc.slice(0, 10))))
            .slice(0, 3);
          setRelatedAlerts(related);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch alert detail from National Weather Service');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 space-y-4">
        <div className="h-6 w-36 bg-slate-200 rounded animate-pulse" />
        <div className="h-72 bg-slate-100 rounded-2xl animate-pulse border border-slate-200" />
      </div>
    );
  }

  if (error || !alert) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center space-y-4">
        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mx-auto border border-red-200">
          <AlertTriangle className="w-7 h-7" />
        </div>
        <h1 className="text-2xl font-black text-navy-950">Bulletin Record Unavailable</h1>
        <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">{error}</p>
        <div className="pt-2">
          <Link to="/alerts" className="px-5 py-2.5 bg-navy-950 text-white rounded-xl text-xs font-bold hover:bg-navy-900 shadow-md">
            Return to Active Radar
          </Link>
        </div>
      </div>
    );
  }

  const active = isAlertActive(alert.expires);
  const visuals = getSeverityVisuals(alert.severity);

  // JSON-LD structured data for emergency announcement
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SpecialAnnouncement',
    'name': alert.event,
    'text': alert.headline || alert.description,
    'datePosted': alert.effective,
    'expires': alert.expires,
    'category': alert.category,
    'spatialCoverage': {
      '@type': 'AdministrativeArea',
      'name': alert.areaDesc,
    },
    'publisher': {
      '@type': 'GovernmentOrganization',
      'name': alert.senderName,
      'url': 'https://www.weather.gov',
    },
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* JSON-LD Script */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Breadcrumb & Action Header */}
      <div className="flex items-center justify-between">
        <Link to="/alerts" className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline font-bold">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Alert Directory</span>
        </Link>
        <button
          onClick={handleShare}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors shadow-2xs"
          title="Copy link to this official alert record"
        >
          <Share2 className="w-3.5 h-3.5" />
          <span>{copied ? 'Link Copied!' : 'Share Record'}</span>
        </button>
      </div>

      {/* Main Official Bulletin Card */}
      <article className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-xs space-y-8">
        {/* Top Badges & Live Status */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-5">
          <div className="flex flex-wrap items-center gap-2">
            <SeverityBadge severity={alert.severity} size="lg" />
            <span className="text-xs font-mono font-semibold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700">
              URGENCY: {alert.urgency.toUpperCase()}
            </span>
            <span className="text-xs font-mono font-medium px-2.5 py-1 rounded-lg bg-slate-50 text-slate-600 border border-slate-200">
              CERTAINTY: {alert.certainty.toUpperCase()}
            </span>
          </div>

          <div>
            {!active ? (
              <span className="text-xs font-mono font-bold text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
                STATUS: EXPIRED
              </span>
            ) : (
              <span className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>STATUS: CURRENTLY ACTIVE</span>
              </span>
            )}
          </div>
        </div>

        {/* Title & Official Headline */}
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-4xl font-black text-navy-950 leading-tight font-sans">
            {alert.event}
          </h1>
          {alert.headline && alert.headline !== alert.event && (
            <p className="text-sm sm:text-base text-slate-700 font-medium leading-relaxed bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/70">
              {alert.headline}
            </p>
          )}
        </div>

        {/* Metadata Telemetry Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-slate-50/90 rounded-2xl border border-slate-200/80 text-xs">
          <div>
            <span className="text-slate-400 font-mono block mb-1 uppercase text-[10px]">Issuing Weather Office</span>
            <span className="font-bold text-navy-900 flex items-center gap-1.5 text-xs sm:text-sm">
              <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span>{alert.senderName}</span>
            </span>
          </div>

          <div>
            <span className="text-slate-400 font-mono block mb-1 uppercase text-[10px]">Effective Broadcast</span>
            <span className="font-semibold text-navy-900 flex items-center gap-1.5 text-xs">
              <Calendar className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <span>{formatDateSafe(alert.effective)}</span>
            </span>
          </div>

          <div>
            <span className="text-slate-400 font-mono block mb-1 uppercase text-[10px]">Official Expiration</span>
            <span className="font-semibold text-navy-900 flex items-center gap-1.5 text-xs">
              <Clock className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <span>{formatDateSafe(alert.expires)}</span>
            </span>
          </div>
        </div>

        {/* Affected Geographic Zones */}
        <div className="space-y-2">
          <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-blue-600" />
            <span>Official Affected Zones &amp; Municipalities</span>
          </h2>
          <div className="p-4 bg-slate-900 text-slate-200 rounded-xl font-mono text-xs leading-relaxed border border-slate-800">
            {alert.areaDesc}
          </div>
        </div>

        {/* WHAT SHOULD I DO NOW? ACTION PANEL */}
        {(() => {
          const guidance = getPlainLanguageGuidance(alert.event, alert.severity);
          return (
            <div className="bg-gradient-to-r from-blue-900 via-navy-900 to-slate-900 text-white rounded-2xl p-6 sm:p-7 shadow-md border border-blue-800/80 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-700/80 pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white">
                    <ShieldAlert className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-black text-white font-sans">
                      {lang === 'en' ? 'What Should I Do Now?' : '¿Qué Debo Hacer Ahora?'}
                    </h2>
                    <p className="text-[11px] text-slate-300 font-sans">
                      {lang === 'en' ? 'Plain-language explanation & immediate action checklist' : 'Explicación en lenguaje sencillo y lista de acciones'}
                    </p>
                  </div>
                </div>

                {/* English / Spanish Toggle */}
                <div className="inline-flex rounded-lg bg-slate-800 p-0.5 border border-slate-700 text-xs font-semibold">
                  <button
                    onClick={() => setLang('en')}
                    className={`px-3 py-1 rounded-md transition-all ${
                      lang === 'en' ? 'bg-blue-600 text-white shadow-2xs font-bold' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    🇺🇸 English
                  </button>
                  <button
                    onClick={() => setLang('es')}
                    className={`px-3 py-1 rounded-md transition-all ${
                      lang === 'es' ? 'bg-blue-600 text-white shadow-2xs font-bold' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    🇪🇸 Español
                  </button>
                </div>
              </div>

              {/* What This Means */}
              <div className="space-y-1">
                <div className="text-[11px] font-mono uppercase tracking-wider text-cyan-400 font-bold">
                  {lang === 'en' ? 'WHAT THIS ALERT MEANS:' : 'QUÉ SIGNIFICA ESTA ALERTA:'}
                </div>
                <p className="text-sm text-slate-200 leading-relaxed font-sans font-medium">
                  {lang === 'en' ? guidance.plainMeaning : guidance.spanishMeaning}
                </p>
              </div>

              {/* Immediate Action Checklist */}
              <div className="space-y-2 pt-2 border-t border-slate-800">
                <div className="text-[11px] font-mono uppercase tracking-wider text-emerald-400 font-bold">
                  {lang === 'en' ? 'IMMEDIATE ACTION CHECKLIST:' : 'LISTA DE ACCIONES INMEDIATAS:'}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-200">
                  {(lang === 'en' ? guidance.actionItems : guidance.spanishActions).map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-slate-800/60 p-2.5 rounded-xl border border-slate-700/60">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Official Safety Instructions (VERBATIM, NEVER FABRICATED) */}
        {alert.instruction ? (
          <div className="space-y-3 p-6 bg-amber-50/90 border border-amber-300/80 rounded-2xl">
            <div className="flex items-center gap-2 font-bold text-amber-950 text-base">
              <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <span>Official NWS Precautionary &amp; Preparedness Actions</span>
            </div>
            <div className="text-xs sm:text-sm text-amber-950 whitespace-pre-line leading-relaxed font-sans">
              {alert.instruction}
            </div>
            <p className="text-[11px] font-mono text-amber-800 italic pt-1 border-t border-amber-200">
              * The protective instructions above are quoted verbatim as published by the National Weather Service.
            </p>
          </div>
        ) : (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
            No specific protective action guidelines were attached to this telegraphic bulletin. Standard severe weather precautions apply.
          </div>
        )}

        {/* Full Official Bulletin Monospace Terminal */}
        {alert.description && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-mono text-slate-500 uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <Terminal className="w-4 h-4 text-slate-400" />
                <span>Full Official Telegraphic Bulletin Text</span>
              </span>
              <span className="text-[10px] text-slate-400">UNALTERED SOURCE TRANSMISSION</span>
            </div>
            <div className="p-5 bg-slate-950 text-slate-300 rounded-2xl border border-slate-800 text-xs font-mono leading-relaxed max-h-96 overflow-y-auto whitespace-pre-line selection:bg-cyan-500 selection:text-navy-950">
              {alert.description}
            </div>
          </div>
        )}

        {/* Geographic Map Visualization */}
        {alert.centroid && (
          <div className="space-y-2">
            <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-cyan-600" />
              <span>Zone Coordinates Plot</span>
            </h2>
            <AlertMap
              alerts={[alert]}
              center={alert.centroid}
              zoom={8}
              height="360px"
            />
          </div>
        )}

        {/* Source Verification Link Footer */}
        <div className="pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
          <div className="font-mono text-[11px] text-slate-500 truncate max-w-sm">
            CAP ID: {alert.id}
          </div>
          <a
            href={alert.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold rounded-xl transition-colors border border-blue-200 shadow-2xs"
          >
            <span>Verify Source on weather.gov</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </article>

      {/* Reserved Ad Slot */}
      <AdSlot slotId="alert-detail-bottom" format="horizontal" />

      {/* Related Active Alerts */}
      {relatedAlerts.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-black text-navy-950">
            Other Active Alerts in This Category
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {relatedAlerts.map((rel) => (
              <div key={rel.id} className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-xs space-y-2.5">
                <SeverityBadge severity={rel.severity} size="sm" />
                <h3 className="font-bold text-xs text-navy-950 line-clamp-2">
                  <Link to={`/alert/${encodeURIComponent(rel.id)}`} className="hover:text-blue-600">
                    {rel.event}
                  </Link>
                </h3>
                <p className="text-[11px] text-slate-500 line-clamp-2">{rel.areaDesc}</p>
                <div className="text-[10px] font-mono text-slate-400">
                  {formatRelativeTime(rel.effective)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Public Safety Advisory */}
      <DisclaimerBanner />
    </div>
  );
};
